import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'
import { NewSignatureField } from './signService'

export type SignatureRequestStatus = 'sent' | 'completed' | 'voided'
export type SignerStatus = 'pending' | 'viewed' | 'signed'

export interface SignatureRequestSummary {
  id: string
  matterId: string
  documentId: string
  fileName: string
  status: SignatureRequestStatus
  createdAt: string
  completedAt?: string
  voidedAt?: string
}

export interface SignatureRequestSigner {
  id: string
  name: string
  email: string
  order: number
  status: SignerStatus
  viewedAt: string | null
  signedAt: string | null
}

export interface NewSigner {
  name: string
  email: string
  fields: NewSignatureField[]
}

// Authenticated: tenant-side signature-request management on a matter they own.
export const useEsignService = () => {
  const { token, authFetch } = useAuth()

  const listSignatureRequests = async (matterId: string): Promise<SignatureRequestSummary[]> => {
    if (!token) return []

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/signature-requests`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to list signature requests: ${response.status}`)
    }

    return data.signatureRequests || []
  }

  const getSignatureRequest = async (
    matterId: string,
    requestId: string,
  ): Promise<{ signatureRequest: SignatureRequestSummary; signers: SignatureRequestSigner[] }> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/signature-requests/${requestId}`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get signature request: ${response.status}`)
    }

    return data
  }

  const createSignatureRequest = async (
    matterId: string,
    documentId: string,
    signers: NewSigner[],
  ): Promise<{ signatureRequest: SignatureRequestSummary; signers: SignatureRequestSigner[] }> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/signature-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentId, signers }),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to create signature request: ${response.status}`)
    }

    return data
  }

  const voidSignatureRequest = async (matterId: string, requestId: string): Promise<void> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/signature-requests/${requestId}/void`, {
      method: 'POST',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new Error(data?.error || `Failed to void signature request: ${response.status}`)
    }
  }

  const getSignatureRequestDownloadUrl = async (matterId: string, requestId: string): Promise<string> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/matters/${matterId}/signature-requests/${requestId}/download-url`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get download link: ${response.status}`)
    }

    return data.downloadUrl
  }

  return {
    listSignatureRequests,
    getSignatureRequest,
    createSignatureRequest,
    voidSignatureRequest,
    getSignatureRequestDownloadUrl,
  }
}
